import { RebateOverview, RebatePayoutStatusCode } from "@/gql/graphql";
import {
  getAchievedGoal,
  getActiveGoal,
  getAuthorizedBy,
  getExpectedGoal,
  getRebateModelCode,
} from "./rebateOverviewUtils";

/**
 * The progressive models are the ones that climb through several levels.
 *
 * These are the codes as they arrive from the backend. Progressive and
 * retroactive is RP in both places, but progressive and linear arrives as P
 * and is only shown as LP, which resolveRebateModel takes care of.
 */
const progressiveModelCodes = ["P", "RP"];

const isStair = (rebate: RebateOverview) =>
  progressiveModelCodes.includes(getRebateModelCode(rebate) ?? "");

const hasReachedTarget = (rebate: RebateOverview) =>
  getExpectedGoal(rebate)?.statusCode === "REACHED";

const isAuthorized = (rebate: RebateOverview) => !!getAuthorizedBy(rebate);

/**
 * The statuses a rebate ends up in once there is nothing left to do to it.
 *
 * A goal carries its status as a plain string, so these are widened to
 * compare against one.
 */
const settledStatusCodes: string[] = [
  RebatePayoutStatusCode.Failed,
  RebatePayoutStatusCode.Paid,
];

/** Read off the active goal, which is the goal the status column shows. */
const isSettled = (rebate: RebateOverview) =>
  settledStatusCodes.includes(getActiveGoal(rebate)?.statusCode ?? "");

/**
 * Releasing gives back what a rebate has reserved, and a settled rebate has
 * nothing left to give back.
 */
export const canReleaseRebate = (rebate: RebateOverview): boolean =>
  !isSettled(rebate);

/**
 * A payout is only made out of what has been reserved, so a rebate with
 * nothing reserved has nothing to pay and is closed by releasing it instead.
 */
const hasReserved = (rebate: RebateOverview) =>
  (rebate.reservedAmount ?? 0) > 0;

/**
 * A stair rebate pays out at the level it reached. A rebate on a model
 * without levels has none above it, so authorizing a payout is what lets it
 * pay out without having reached its target.
 *
 * A settled rebate has been paid already, and an authorization stays stamped
 * on its level once it has been used, so settlement has to close the payout
 * rather than the authorization running out.
 */
const hasEarnedPayout = (rebate: RebateOverview) =>
  !isSettled(rebate) &&
  (isStair(rebate)
    ? hasReachedTarget(rebate)
    : hasReachedTarget(rebate) || isAuthorized(rebate));

/**
 * Paying out above the level reached only means something on a stair that
 * has not reached its level yet, has not been settled, and only once someone
 * has authorized it.
 */
const hasEarnedPayoutOnHigherLevel = (rebate: RebateOverview) =>
  !isSettled(rebate) &&
  isStair(rebate) &&
  !hasReachedTarget(rebate) &&
  isAuthorized(rebate);

export const canPayOutRebate = (rebate: RebateOverview): boolean =>
  hasEarnedPayout(rebate) && hasReserved(rebate);

export const canPayOutOnHigherLevel = (rebate: RebateOverview): boolean =>
  hasEarnedPayoutOnHigherLevel(rebate) && hasReserved(rebate);

/**
 * Authorizing is what lets a rebate pay out that has not earned it yet, so
 * there is nothing left to authorize once either payout is earned - whether
 * that is because it reached its level or because someone has already
 * authorized it. That holds while nothing is reserved too, since what holds
 * the payout back then is not a missing authorization.
 *
 * The authorization is stamped on the level the expected volume covers, so
 * there is also nothing to authorize while it covers none, and nothing to
 * authorize a payout for once the rebate is settled.
 */
export const canAuthorizePayment = (rebate: RebateOverview): boolean =>
  !isSettled(rebate) &&
  !!getExpectedGoal(rebate) &&
  !hasEarnedPayout(rebate) &&
  !hasEarnedPayoutOnHigherLevel(rebate);

/**
 * The level a payout is made at, for every model and both payout actions.
 *
 * A stair is paid at the level its expected volume covers, which is the
 * level an authorization is stamped on, so paying out above the level
 * reached pays the level that was authorized rather than the lowest one.
 * Climbing past what was expected earns the higher level, so a rebate that
 * achieves more than it was expected to is paid at what it achieved.
 *
 * A rebate on a model without levels has only the one goal, which both
 * sides resolve to.
 *
 * There is no level to pay when neither the expected nor the achieved
 * volume covers even the lowest one, which is why callers have to handle
 * this being undefined.
 */
export const getPayoutGoal = (rebate: RebateOverview) => {
  const achieved = getAchievedGoal(rebate);
  const expected = getExpectedGoal(rebate);

  if (!achieved) {
    return expected;
  }
  if (!expected) {
    return achieved;
  }

  return achieved.targetValue > expected.targetValue ? achieved : expected;
};
